import React, { useRef, useEffect, useState } from "react";
import { useForm } from "@formspree/react";
import SEO from "../components/SEO";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Logo } from "../components/UI";

/* ── reveal hook ── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ── services list for checkbox grid ── */
const SERVICES_LIST = [
  "Branding", "Graphic Design", "Website", "E-commerce",
  "Content Creation", "Production / Ad Shoots", "Social Media",
  "Meta Ads", "Google Ads", "SEO",
  "Marketing Strategy", "Automation",
  "Something else", "Not sure yet",
];

const BUDGET_OPTIONS = [
  "Not decided", "Under ₹50K", "₹50K–₹1L", "₹1L–₹3L", "₹3L+", "Let's discuss",
];

const NEXT_STEPS = [
  { n: "01", title: "You tell us", body: "Your business, idea or challenge." },
  { n: "02", title: "We understand", body: "We look at what you're actually trying to achieve." },
  { n: "03", title: "We talk", body: "We discuss the right direction together." },
  { n: "04", title: "We build", body: "Once we're aligned, we get to work." },
];

const FAQS = [
  {
    q: "What does OVARD Digital do?",
    a: "OVARD Digital works across branding, design, websites, e-commerce, content, production, social media, advertising, SEO, marketing strategy and automation.",
  },
  {
    q: "Do I need to know exactly what service I need?",
    a: "No. Tell us about your business, goal or challenge. We'll help identify what makes sense for you.",
  },
  {
    q: "Do you work with new businesses?",
    a: "Yes. We can build the digital foundation around a new business — from branding and website to content and marketing.",
  },
  {
    q: "Can you work with an existing brand?",
    a: "Yes. We can work with an existing brand to improve its digital presence, content, marketing or overall growth strategy.",
  },
  {
    q: "Do you provide website and e-commerce development?",
    a: "Yes. We build websites and online stores based on the business's requirements, goals and scale.",
  },
  {
    q: "Do you handle content shoots and advertisements?",
    a: "Yes. Our creative and production capabilities include content creation, product shoots, brand shoots, ad shoots, campaign shoots and advertisement production.",
  },
  {
    q: "Can you manage social media and advertising together?",
    a: "Yes. Social media management, creative content and paid advertising can be brought together as part of a broader strategy.",
  },
  {
    q: "Do you offer one-time projects or ongoing services?",
    a: "Both. Some businesses need a specific project such as a website or brand identity, while others need ongoing content, social media or marketing support.",
  },
  {
    q: "How do we get started?",
    a: "Send us an enquiry through the contact form or reach out directly on WhatsApp or email. We'll start by understanding what you're trying to achieve.",
  },
  {
    q: "Where do you work?",
    a: "OVARD can work with businesses remotely, while production and shoot requirements can be discussed based on the project.",
  },
];

/* ════════════════════════════════════════
   PAGE
════════════════════════════════════════ */
export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const contactSchema = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": "https://ovarddigital.com/contact#webpage",
      "url": "https://ovarddigital.com/contact",
      "name": "Contact OVARD Digital — Get in Touch",
      "description": "Contact OVARD Digital, a digital marketing and brand growth agency in Mysore, India. Reach us by WhatsApp, email or the contact form to start a conversation about your project.",
      "isPartOf": { "@id": "https://ovarddigital.com/#website" },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ovarddigital.com/" },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://ovarddigital.com/contact" }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }
  ];

  return (
    <div className="cp-page">
      <SEO
        title="Contact OVARD Digital — Start a Conversation"
        description="Get in touch with OVARD Digital, a digital marketing and brand growth agency in Mysore, India. Contact us by WhatsApp at +91 7975034116, email at ovarddigital@gmail.com, or use the contact form."
        canonical="/contact"
        schema={contactSchema}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" }
        ]}
      />
      <Nav />
      <ContactHero />
      <ContactMain />
      <NextStepsSection />
      <FAQSection />
      <NotSureSection />
      <FinalStatement />
      <Footer />
    </div>
  );
}

/* ── 1. Hero ── */
function ContactHero() {
  const [play, setPlay] = useState(false);
  useEffect(() => { const t = setTimeout(() => setPlay(true), 60); return () => clearTimeout(t); }, []);

  return (
    <section className={"cp-hero" + (play ? " cp-hero--play" : "")}>
      <div className="cp-hero-grain" aria-hidden="true" />
      <div className="cp-hero-glow" aria-hidden="true" />

      <div className="container cp-hero-inner">
        <div className="cp-hero-eyebrow">
          <span className="cp-hero-dot" />
          LET'S TALK
        </div>
        <h1 className="cp-hero-h1">
          <span className="cp-hero-line"><span>Have something</span></span>
          <span className="cp-hero-line cp-hero-line--indent"><span>worth building?</span></span>
        </h1>
        <p className="cp-hero-sub">
          Tell us what you're working on. You don't need to have everything figured out — start with the idea, business or challenge.
        </p>

        <div className="cp-hero-channels">
          <a href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" className="cp-hero-channel">
            <span className="cp-hero-channel-label">WhatsApp</span>
            <span className="cp-hero-channel-val">7975034116</span>
            <span className="cp-hero-channel-arr">↗</span>
          </a>
          <a href="mailto:ovarddigital@gmail.com" className="cp-hero-channel">
            <span className="cp-hero-channel-label">Email</span>
            <span className="cp-hero-channel-val">ovarddigital@gmail.com</span>
            <span className="cp-hero-channel-arr">↗</span>
          </a>
          <a href="https://www.instagram.com/ovardigital/" target="_blank" rel="noopener noreferrer" className="cp-hero-channel">
            <span className="cp-hero-channel-label">Instagram</span>
            <span className="cp-hero-channel-val">@ovardigital</span>
            <span className="cp-hero-channel-arr">↗</span>
          </a>
          <a href="https://www.linkedin.com/company/ovard-digital/" target="_blank" rel="noopener noreferrer" className="cp-hero-channel">
            <span className="cp-hero-channel-label">LinkedIn</span>
            <span className="cp-hero-channel-val">OVARD Digital</span>
            <span className="cp-hero-channel-arr">↗</span>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── 2. Main contact + form ── */
function ContactMain() {
  const [ref, visible] = useReveal(0.05);
  return (
    <section className={"cp-main" + (visible ? " cp-main--visible" : "")} ref={ref}>
      <div className="container cp-main-grid">
        <ContactLeft />
        <ContactForm />
      </div>
    </section>
  );
}

function ContactLeft() {
  return (
    <div className="cp-left">
      <span className="cp-eyebrow">Let's Talk</span>
      <h2 className="cp-left-h2">Reach us directly.<br />We respond fast.</h2>

      <div className="cp-channels">
        <a href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" className="cp-channel">
          <div className="cp-channel-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M20.52 3.48A11.93 11.93 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.22-3.48-8.52zm-8.52 18.43a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.74.98 1-3.65-.24-.38A9.9 9.9 0 0 1 2.1 12C2.1 6.53 6.53 2.1 12 2.1c2.65 0 5.14 1.03 7.01 2.9a9.87 9.87 0 0 1 2.9 7.01c-.01 5.46-4.44 9.9-9.91 9.9zm5.43-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.65-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.34z" fill="currentColor"/>
            </svg>
          </div>
          <div className="cp-channel-info">
            <span className="cp-channel-label">WhatsApp</span>
            <span className="cp-channel-val">7975034116</span>
            <span className="cp-channel-cta">Chat on WhatsApp →</span>
          </div>
        </a>

        <a href="mailto:ovarddigital@gmail.com" className="cp-channel">
          <div className="cp-channel-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M4 6.5L12 13L20 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="cp-channel-info">
            <span className="cp-channel-label">Email</span>
            <span className="cp-channel-val">ovarddigital@gmail.com</span>
            <span className="cp-channel-cta">Send an email →</span>
          </div>
        </a>

        <a href="https://www.instagram.com/ovardigital/" target="_blank" rel="noopener noreferrer" className="cp-channel">
          <div className="cp-channel-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3.5" y="3.5" width="17" height="17" rx="5" stroke="currentColor" strokeWidth="1.4"/>
              <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.4"/>
              <circle cx="17.2" cy="6.8" r="1" fill="currentColor"/>
            </svg>
          </div>
          <div className="cp-channel-info">
            <span className="cp-channel-label">Instagram</span>
            <span className="cp-channel-val">@ovardigital</span>
            <span className="cp-channel-cta">Visit Instagram →</span>
          </div>
        </a>

        <a href="https://www.linkedin.com/company/ovard-digital/" target="_blank" rel="noopener noreferrer" className="cp-channel">
          <div className="cp-channel-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="2" width="20" height="20" rx="3" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M7 10v7M7 7v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <path d="M11 17v-4a2 2 0 0 1 4 0v4M11 10v7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="cp-channel-info">
            <span className="cp-channel-label">LinkedIn</span>
            <span className="cp-channel-val">OVARD Digital</span>
            <span className="cp-channel-cta">Connect on LinkedIn →</span>
          </div>
        </a>
      </div>

      <div className="cp-left-note">
        <span className="cp-left-note-dot" />
        <p>Not sure what you need? Tell us what you're trying to achieve and we'll help figure out the right direction.</p>
      </div>

      {/* Availability badge */}
      <div className="cp-avail">
        <span className="cp-avail-dot" />
        <span className="cp-avail-text">Available for new projects</span>
      </div>
    </div>
  );
}

function ContactForm() {
  const [state, handleFormspreeSubmit] = useForm("mgaezjqo");
  const [selected, setSelected] = useState([]);
  const [budget, setBudget] = useState("");
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", message: "" });

  const toggle = (s) => setSelected((prev) =>
    prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
  );

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const services = selected.length ? selected.join(", ") : "Not specified";

    // Submit to Formspree — triggers email to ovarddigital@gmail.com
    // + auto-reply to the user (configure in Formspree dashboard)
    await handleFormspreeSubmit({
      name: form.name,
      business: form.business,
      phone: form.phone,
      email: form.email,
      services,
      budget: budget || "Not specified",
      message: form.message,
    });
  };

  if (state.succeeded) {
    return (
      <div className="cp-form-wrap">
        <div className="cp-success-card">
          {/* Logo */}
          <div className="cp-success-logo">
            <Logo height={36} />
          </div>

          {/* Check */}
          <div className="cp-success-check" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="14" fill="rgba(201,150,46,0.12)"/>
              <path d="M8 14.5l4 4 8-8" stroke="#c9962e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <h3 className="cp-success-h3">Message received.</h3>

          <p className="cp-success-body">
            Thank you for reaching out. We've received your details and will get back to you shortly. Check your inbox — you should have a confirmation from us.
          </p>

          <div className="cp-success-divider" />

          <p className="cp-success-tagline">Your Brand. Our Strategy. Unlimited Growth.</p>

          {/* Reach out directly */}
          <div className="cp-success-channels">
            <a href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" className="cp-success-channel">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.22-3.48-8.52zm-8.52 18.43a9.9 9.9 0 0 1-5.04-1.38l-.36-.22-3.74.98 1-3.65-.24-.38A9.9 9.9 0 0 1 2.1 12C2.1 6.53 6.53 2.1 12 2.1c2.65 0 5.14 1.03 7.01 2.9a9.87 9.87 0 0 1 2.9 7.01c-.01 5.46-4.44 9.9-9.91 9.9zm5.43-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.65-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.27.49 1.7.63.72.23 1.37.2 1.88.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.34z"/>
              </svg>
              WhatsApp
            </a>
            <span className="cp-success-sep">·</span>
            <a href="mailto:ovarddigital@gmail.com" className="cp-success-channel">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="5" width="18" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M4 6.5L12 13L20 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Email
            </a>
            <span className="cp-success-sep">·</span>
            <a href="https://www.instagram.com/ovardigital/" target="_blank" rel="noopener noreferrer" className="cp-success-channel cp-success-channel--insta">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="3.5" y="3.5" width="17" height="17" rx="5" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.4"/>
                <circle cx="17.2" cy="6.8" r="1" fill="currentColor"/>
              </svg>
              Follow on Instagram
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-form-wrap">
      <div className="cp-form-head">
        <span className="cp-eyebrow">Tell Us About It</span>
        <h2 className="cp-form-h2">What are you building?</h2>
      </div>

      <form className="cp-form" onSubmit={handleSubmit} noValidate>

        {/* Row 1 */}
        <div className="cp-form-row">
          <div className="cp-field">
            <label htmlFor="cp-name">Name <span aria-hidden="true">*</span></label>
            <input
              id="cp-name" name="name" type="text"
              placeholder="Your name"
              value={form.name} onChange={handleChange} required
            />
          </div>
          <div className="cp-field">
            <label htmlFor="cp-business">Business / Brand</label>
            <input
              id="cp-business" name="business" type="text"
              placeholder="Business or brand name"
              value={form.business} onChange={handleChange}
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="cp-form-row">
          <div className="cp-field">
            <label htmlFor="cp-phone">Phone / WhatsApp</label>
            <input
              id="cp-phone" name="phone" type="tel"
              placeholder="+91 00000 00000"
              value={form.phone} onChange={handleChange}
            />
          </div>
          <div className="cp-field">
            <label htmlFor="cp-email">Email</label>
            <input
              id="cp-email" name="email" type="email"
              placeholder="you@example.com"
              value={form.email} onChange={handleChange}
            />
          </div>
        </div>

        {/* Services */}
        <div className="cp-field cp-field--full">
          <label>What are you looking for?</label>
          <div className="cp-services-grid">
            {SERVICES_LIST.map((s) => (
              <button
                type="button"
                key={s}
                className={"cp-service-chip" + (selected.includes(s) ? " cp-service-chip--on" : "")}
                onClick={() => toggle(s)}
                aria-pressed={selected.includes(s)}
              >
                {selected.includes(s) && <span className="cp-chip-check" aria-hidden="true">✓ </span>}
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div className="cp-field cp-field--full">
          <label htmlFor="cp-message">Tell us more</label>
          <textarea
            id="cp-message" name="message" rows={4}
            placeholder="What are you trying to build, improve or grow?"
            value={form.message} onChange={handleChange}
          />
        </div>

        {/* Budget */}
        <div className="cp-field cp-field--full">
          <label>Budget / Investment Range <span className="cp-label-opt">(optional)</span></label>
          <div className="cp-budget-grid">
            {BUDGET_OPTIONS.map((b) => (
              <button
                type="button"
                key={b}
                className={"cp-budget-chip" + (budget === b ? " cp-budget-chip--on" : "")}
                onClick={() => setBudget(budget === b ? "" : b)}
                aria-pressed={budget === b}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        <button type="submit" className="cp-submit-btn" disabled={state.submitting}>
          {state.submitting ? "Sending..." : "Start the conversation"}
          {!state.submitting && (
            <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
              <path d="M9 1L14 5.5L9 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 5.5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          )}
        </button>

        <p className="cp-form-note">
          We'll reply within 24 hours. You'll also receive a confirmation email from us.
        </p>

      </form>
    </div>
  );
}

/* ── 3. What happens next ── */
function NextStepsSection() {
  const [ref, visible] = useReveal(0.1);
  return (
    <section className={"cp-next" + (visible ? " cp-next--visible" : "")} ref={ref}>
      <div className="container">
        <div className="cp-next-head">
          <span className="cp-eyebrow cp-fade-up" style={{ transitionDelay: "0s" }}>What Happens Next?</span>
          <h2 className="cp-next-h2 cp-fade-up" style={{ transitionDelay: "0.08s" }}>
            Simple from start to finish.
          </h2>
        </div>
        <div className="cp-next-steps">
          {NEXT_STEPS.map((s, i) => (
            <div className="cp-next-step cp-fade-up" key={s.n} style={{ transitionDelay: `${0.14 + i * 0.1}s` }}>
              <span className="cp-next-num">{s.n}</span>
              <h4 className="cp-next-title">{s.title}</h4>
              <p className="cp-next-body">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── 4. FAQ accordion ── */
function FAQSection() {
  const [ref, visible] = useReveal(0.08);
  const [open, setOpen] = useState(null);

  return (
    <section className={"cp-faq" + (visible ? " cp-faq--visible" : "")} ref={ref}>
      <div className="container cp-faq-inner">

        <div className="cp-faq-head">
          <span className="cp-eyebrow cp-fade-up" style={{ transitionDelay: "0s" }}>FAQ</span>
          <h2 className="cp-faq-h2 cp-fade-up" style={{ transitionDelay: "0.08s" }}>
            Questions, answered.
          </h2>
        </div>

        <div className="cp-faq-list">
          {FAQS.map((item, i) => (
            <div
              className={"cp-faq-item cp-fade-up" + (open === i ? " cp-faq-item--open" : "")}
              key={item.q}
              style={{ transitionDelay: `${0.06 + i * 0.04}s` }}
            >
              <button
                className="cp-faq-q"
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
              >
                <span>{item.q}</span>
                <span className="cp-faq-icon" aria-hidden="true">
                  {open === i ? "−" : "+"}
                </span>
              </button>
              <div className="cp-faq-a-wrap" style={{ maxHeight: open === i ? "400px" : "0" }}>
                <p className="cp-faq-a">{item.a}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ── 5. Not sure ── */
function NotSureSection() {
  const [ref, visible] = useReveal(0.2);
  return (
    <section className={"cp-notsure" + (visible ? " cp-notsure--visible" : "")} ref={ref}>
      <div className="container cp-notsure-inner">
        <div className="cp-notsure-text">
          <span className="cp-eyebrow" style={{ color: "var(--gold)" }}>Not sure what you need?</span>
          <h2 className="cp-notsure-h2">
            You don't need a perfect brief.
          </h2>
          <p className="cp-notsure-body">
            Start with the problem. We'll figure out the solution together.
          </p>
        </div>
        <a href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" className="cp-notsure-cta">
          <span>Message us on WhatsApp</span>
          <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
            <path d="M9 1L14 5.5L9 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 5.5H1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </a>
      </div>
    </section>
  );
}

/* ── 6. Final statement ── */
function FinalStatement() {
  const [ref, visible] = useReveal(0.2);
  return (
    <section className={"cp-final" + (visible ? " cp-final--visible" : "")} ref={ref}>
      <div className="cp-final-grain" aria-hidden="true" />
      <div className="cp-final-glow" aria-hidden="true" />
      <div className="container cp-final-inner">
        <div className="cp-final-mark">
          Your Brand.<br />
          <span>Our Strategy.</span><br />
          Unlimited Growth.
        </div>
        <div className="cp-final-contacts">
          <a href="https://wa.me/917975034116" target="_blank" rel="noopener noreferrer" className="cp-final-contact-link">
            7975034116
          </a>
          <span className="cp-final-sep">·</span>
          <a href="mailto:ovarddigital@gmail.com" className="cp-final-contact-link">
            ovarddigital@gmail.com
          </a>
          <span className="cp-final-sep">·</span>
          <a href="https://www.instagram.com/ovardigital/" target="_blank" rel="noopener noreferrer" className="cp-final-contact-link">
            @ovardigital
          </a>
          <span className="cp-final-sep">·</span>
          <a href="https://www.linkedin.com/company/ovard-digital/" target="_blank" rel="noopener noreferrer" className="cp-final-contact-link">
            LinkedIn
          </a>
        </div>
      </div>
    </section>
  );
}
